import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Modal,
  SidePanel,
  type BadgeVariant,
  type ButtonVariant,
} from '@silverfalls/sterling';
import { DetailRow } from '../components/DetailRow.js';
import { useToast } from '../components/Toast.js';
import {
  fetchVendor,
  legalNextStates,
  updateVendor,
  updateVendorState,
  type Form1099FormType,
  type Form1099Kind,
  type PaymentMethod,
  type PaymentTerms,
  type TaxExemptionKind,
  type UpdateVendorInput,
  type VendorDetail as VendorDetailShape,
  type VendorState,
} from '../api/p2p.js';

const REQUESTING_USER = 'u-ui-skeleton';

export function VendorDetail(): JSX.Element {
  const { ref } = useParams<{ ref: string }>();
  const toast = useToast();
  const [vendor, setVendor] = useState<VendorDetailShape | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actioning, setActioning] = useState<VendorState | null>(null);

  const load = useCallback(async () => {
    if (!ref) return;
    setLoading(true);
    setError(null);
    try {
      const v = await fetchVendor(ref);
      setVendor(v);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [ref]);

  useEffect(() => {
    void load();
  }, [load]);

  const [pendingDestructive, setPendingDestructive] = useState<VendorState | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  const doTransition = useCallback(
    async (toState: VendorState) => {
      if (!ref || !vendor) return;
      setActioning(toState);
      setActionError(null);
      try {
        const result = await updateVendorState({
          vendorRef: ref,
          toState,
          requestingUser: REQUESTING_USER,
        });
        if (result.__typename === 'UpdateVendorStateFailure') {
          const msg = `${result.class}: ${JSON.stringify(result.details)}`;
          setActionError(msg);
          toast.push(msg, 'error');
        } else {
          toast.push(`${result.fromState} → ${result.toState}`, 'success');
          await load();
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setActionError(msg);
        toast.push(msg, 'error');
      } finally {
        setActioning(null);
      }
    },
    [ref, vendor, load, toast],
  );

  const onTransition = useCallback(
    async (toState: VendorState) => {
      if (!ref || !vendor) return;
      // Only RETIRE is irreversible and gets the Sterling type-to-confirm
      // modal (Sterling §6.2). MERGE is reversible — a plain confirm is
      // enough; it sets a state, doesn't destroy history.
      if (toState === 'RETIRED' || toState === 'MERGED') {
        setPendingDestructive(toState);
        return;
      }
      await doTransition(toState);
    },
    [ref, vendor, doTransition],
  );

  if (loading) return <div className="t-body-sm">Loading vendor…</div>;
  if (error) {
    return (
      <div className="alert alert-neg" role="alert">
        Couldn't load vendor: {error}
      </div>
    );
  }
  if (!vendor) {
    return (
      <div className="empty-state">
        <p>Vendor not found: {ref}</p>
        <p>
          <Link to="/vendors">Back to vendors</Link>
        </p>
      </div>
    );
  }

  const profile = vendor.paymentProfile;

  return (
    <section className="vendor-detail">
      <div className="screen-header">
        <div className="entity-header">
          <Link to="/vendors" className="back-link">
            ← Vendors
          </Link>
          <h2>{vendor.legalName}</h2>
          <div className="entity-ref mono">{vendor.vendorRef}</div>
          <EntitySummary vendor={vendor} />
        </div>
        <div className="header-right-stack">
          <div className="state-actions">
            {vendor.state !== 'RETIRED' && vendor.state !== 'MERGED' && (
              <Button
                variant="secondary"
                size="sm"
                icon="pencil"
                onClick={() => setEditOpen(true)}
                disabled={actioning !== null}
              >
                Edit
              </Button>
            )}
            {legalNextStates(vendor.state).map((next) => (
              <Button
                key={next}
                variant={stateActionVariant(next)}
                size="sm"
                onClick={() => onTransition(next)}
                disabled={actioning !== null}
                loading={actioning === next}
              >
                {stateLabel(next)}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {actionError && (
        <div className="alert alert-neg" role="alert">
          {actionError}
        </div>
      )}

      {(vendor.state === 'RETIRED' || vendor.state === 'MERGED') && (
        <div className="alert alert-warn" role="status">
          <strong>This vendor is {vendor.state.toLowerCase()}.</strong>{' '}
          {vendor.state === 'RETIRED'
            ? 'New purchase orders, requisitions, and invoices against this vendor will be rejected (VENDOR_NOT_ACTIVE).'
            : 'This vendor was merged into another record. Historical references stay; new activity uses the surviving vendor.'}
        </div>
      )}
      {vendor.state === 'SUSPENDED' && (
        <div className="alert alert-warn" role="status">
          <strong>This vendor is suspended.</strong> New POs / invoices will
          be rejected until the vendor is returned to ACTIVE.
        </div>
      )}

      <div className="detail-grid">
        <DetailCard title="Vendor profile">
          <DetailRow label="Display name" value={vendor.displayName} />
          <DetailRow label="Tax ID" value={vendor.taxId} mono />
          <DetailRow label="OPCO" value={vendor.opcoRef} mono />
          <DetailRow
            label="Address"
            value={
              vendor.addressLine2
                ? `${vendor.addressLine1}, ${vendor.addressLine2}, ${vendor.city}, ${vendor.region} ${vendor.postalCode}, ${vendor.country}`
                : `${vendor.addressLine1}, ${vendor.city}, ${vendor.region} ${vendor.postalCode}, ${vendor.country}`
            }
          />
          <DetailRow label="Email" value={vendor.contactEmail ?? '—'} />
          <DetailRow label="Phone" value={vendor.contactPhone ?? '—'} />
          <DetailRow label="Created" value={formatDateTime(vendor.createdAt)} mono />
        </DetailCard>

        {profile ? (
          <DetailCard title="Payment profile">
            <DetailRow label="Terms" value={profile.paymentTerms} />
            <DetailRow label="Method" value={profile.paymentMethod} />
            <DetailRow label="Default GL account" value={profile.defaultGLAccount} mono />
            <DetailRow
              label="1099 status"
              value={
                profile.form1099Status.kind === 'REQUIRED'
                  ? `Required (${profile.form1099Status.formType ?? '?'})`
                  : 'Not required'
              }
            />
            <DetailRow
              label="Tax exemption"
              value={
                profile.taxExemptionStatus.kind === 'EXEMPT'
                  ? `Exempt (cert: ${profile.taxExemptionStatus.certificateRef ?? '?'})`
                  : 'Taxable'
              }
            />
            <DetailRow
              label="Payment hold flags"
              value={profile.paymentHoldFlags.length === 0 ? 'None' : profile.paymentHoldFlags.join(', ')}
            />
            <DetailRow
              label="AP hold flags"
              value={profile.apHoldFlags.length === 0 ? 'None' : profile.apHoldFlags.join(', ')}
            />
            <DetailRow label="Profile ref" value={profile.profileRef} mono />
          </DetailCard>
        ) : (
          <DetailCard title="Payment profile">
            <p className="cell-secondary">No profile.</p>
          </DetailCard>
        )}
      </div>

      <Modal
        open={pendingDestructive !== null}
        title={
          pendingDestructive === 'MERGED'
            ? `Merge ${vendor.legalName}`
            : `Retire ${vendor.legalName}`
        }
        variant={pendingDestructive === 'RETIRED' ? 'danger' : 'default'}
        confirmLabel={pendingDestructive === 'MERGED' ? 'Merge vendor' : 'Retire vendor'}
        // Type-to-confirm only on the irreversible action. MERGE is a
        // reversible state change — a plain confirm is enough.
        {...(pendingDestructive === 'RETIRED' ? { typeToConfirm: vendor.legalName } : {})}
        onCancel={() => setPendingDestructive(null)}
        onConfirm={() => {
          const next = pendingDestructive;
          setPendingDestructive(null);
          if (next) void doTransition(next);
        }}
      >
        {pendingDestructive === 'RETIRED'
          ? 'Retiring this vendor is terminal. New purchase orders, requisitions, and invoices against it will be rejected (VENDOR_NOT_ACTIVE). Historical references stay intact. Type the legal name to confirm.'
          : 'Merging marks this vendor as superseded by another. Historical references stay; new activity must use the surviving vendor.'}
      </Modal>

      {editOpen && (
        <EditVendorDialog
          vendor={vendor}
          onClose={() => setEditOpen(false)}
          onSaved={(changedFields) => {
            setEditOpen(false);
            if (changedFields.length === 0) {
              toast.push('No changes', 'info');
            } else {
              toast.push(`Saved ${changedFields.length} change(s)`, 'success');
            }
            void load();
          }}
          onError={(message) => toast.push(message, 'error')}
        />
      )}
    </section>
  );
}

function EntitySummary({ vendor }: { readonly vendor: VendorDetailShape }): JSX.Element {
  // Inline 3–5 facts the user came to find — state, location, terms, method.
  // Sterling density principle: surface the truths in one line rather than
  // hunting for them in cards below. Numbers in mono if/when summary stats
  // get attached (e.g. "$1.24M paid YTD · 14 invoices open").
  const profile = vendor.paymentProfile;
  const parts: JSX.Element[] = [];
  parts.push(
    <Badge key="state" variant={stateBadgeVariant(vendor.state)} dot={false}>
      {vendor.state}
    </Badge>,
  );
  parts.push(<span key="loc">{`${vendor.city}, ${vendor.region}`}</span>);
  if (profile) {
    parts.push(<span key="terms">{profile.paymentTerms}</span>);
    parts.push(<span key="method">{profile.paymentMethod}</span>);
  }
  return (
    <div className="entity-summary">
      {parts.map((p, i) => (
        <span key={i} className="entity-summary-part">
          {p}
        </span>
      ))}
    </div>
  );
}

function DetailCard({ title, children }: { readonly title: string; readonly children: React.ReactNode }) {
  return (
    <div className="detail-card">
      <h3 className="detail-card-title">{title}</h3>
      <div className="detail-rows">{children}</div>
    </div>
  );
}


function stateBadgeVariant(state: VendorDetailShape['state']): BadgeVariant {
  switch (state) {
    case 'ACTIVE':
      return 'success';
    case 'ONBOARDING':
    case 'PROSPECT':
      return 'info';
    case 'SUSPENDED':
      return 'warning';
    case 'RETIRED':
    case 'MERGED':
      return 'neutral';
  }
}

function stateActionVariant(state: VendorState): ButtonVariant {
  // Only RETIRE is destructive — it's the one irreversible action. SUSPEND
  // and MERGE are both reversible state changes; stacking destructive
  // styling on all three teaches operators to ignore the red.
  switch (state) {
    case 'ACTIVE':
      return 'primary';
    case 'RETIRED':
      return 'destructive';
    case 'SUSPENDED':
    case 'MERGED':
    case 'PROSPECT':
    case 'ONBOARDING':
      return 'secondary';
  }
}

function formatDateTime(iso: string): string {
  return new Date(iso).toISOString().slice(0, 19).replace('T', ' ');
}

function stateLabel(state: VendorState): string {
  switch (state) {
    case 'ACTIVE':
      return 'Activate';
    case 'SUSPENDED':
      return 'Suspend';
    case 'RETIRED':
      return 'Retire';
    case 'MERGED':
      return 'Mark merged';
    case 'PROSPECT':
      return 'Mark prospect';
    case 'ONBOARDING':
      return 'Move to onboarding';
  }
}

// ---------------------------------------------------------------------------
// Edit vendor — SidePanel drawer matching the create-flow pattern.
// Pre-fills from current values; submits only the diff (sends just the
// fields the user actually changed). Honors the gadget's "patch" semantics —
// undefined leaves a field, null clears it (via the `clear` list).
// ---------------------------------------------------------------------------

const PAYMENT_TERMS: ReadonlyArray<PaymentTerms> = [
  'NET_15',
  'NET_30',
  'NET_45',
  'NET_60',
  'NET_90',
  'TWO_TEN_NET_30',
  'DUE_ON_RECEIPT',
];

const PAYMENT_METHODS: ReadonlyArray<PaymentMethod> = ['ACH', 'CHECK', 'WIRE', 'VIRTUAL_CARD'];

const FORM_1099_FORM_TYPES: ReadonlyArray<{ value: Form1099FormType; label: string }> = [
  { value: 'FORM_1099_MISC', label: '1099-MISC' },
  { value: 'FORM_1099_NEC', label: '1099-NEC' },
  { value: 'FORM_1099_INT', label: '1099-INT' },
  { value: 'FORM_1099_DIV', label: '1099-DIV' },
];

interface EditVendorDialogProps {
  readonly vendor: VendorDetailShape;
  readonly onClose: () => void;
  readonly onSaved: (changedFields: ReadonlyArray<string>) => void;
  readonly onError: (message: string) => void;
}

function EditVendorDialog({
  vendor,
  onClose,
  onSaved,
  onError,
}: EditVendorDialogProps): JSX.Element {
  const profile = vendor.paymentProfile;
  // Form state. Strings start from the current vendor; profile selects
  // start from the profile (or sensible defaults if no profile exists).
  const [displayName, setDisplayName] = useState(vendor.displayName);
  const [taxId, setTaxId] = useState(vendor.taxId);
  const [addressLine1, setAddressLine1] = useState(vendor.addressLine1);
  const [addressLine2, setAddressLine2] = useState(vendor.addressLine2 ?? '');
  const [city, setCity] = useState(vendor.city);
  const [region, setRegion] = useState(vendor.region);
  const [postalCode, setPostalCode] = useState(vendor.postalCode);
  const [country, setCountry] = useState(vendor.country);
  const [contactEmail, setContactEmail] = useState(vendor.contactEmail ?? '');
  const [contactPhone, setContactPhone] = useState(vendor.contactPhone ?? '');
  const [paymentTerms, setPaymentTerms] = useState<PaymentTerms>(
    profile?.paymentTerms ?? 'NET_30',
  );
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(
    profile?.paymentMethod ?? 'ACH',
  );
  const [defaultGLAccount, setDefaultGLAccount] = useState(
    profile?.defaultGLAccount ?? '6100-MRO',
  );
  const [form1099Kind, setForm1099Kind] = useState<Form1099Kind>(
    profile?.form1099Status.kind ?? 'NOT_REQUIRED',
  );
  const [form1099FormType, setForm1099FormType] = useState<Form1099FormType>(() => {
    if (profile?.form1099Status.kind === 'REQUIRED' && profile.form1099Status.formType) {
      return profile.form1099Status.formType as Form1099FormType;
    }
    return 'FORM_1099_MISC';
  });
  const [taxKind, setTaxKind] = useState<TaxExemptionKind>(
    profile?.taxExemptionStatus.kind ?? 'TAXABLE',
  );
  const [certificateRef, setCertificateRef] = useState<string>(
    profile?.taxExemptionStatus.kind === 'EXEMPT'
      ? (profile.taxExemptionStatus.certificateRef ?? '')
      : '',
  );

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const onSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setSubmitting(true);
      setError(null);

      // Build only the diff in a mutable working object, then cast at the
      // end. Nullable fields: trimmed-empty means "clear" (tracked in
      // `clear`), populated means "set".
      type MutableInput = {
        -readonly [K in keyof UpdateVendorInput]: UpdateVendorInput[K];
      };
      const working: MutableInput = {
        vendorRef: vendor.vendorRef,
        requestingUser: REQUESTING_USER,
      };
      const clear: Array<'addressLine2' | 'contactEmail' | 'contactPhone'> = [];
      const setIfStringChanged = (
        key: keyof UpdateVendorInput,
        next: string,
        current: string,
      ): void => {
        if (next !== current) (working as Record<string, unknown>)[key] = next;
      };
      const setIfNullableChanged = (
        key: 'addressLine2' | 'contactEmail' | 'contactPhone',
        next: string,
        current: string | null | undefined,
      ): void => {
        const trimmed = next.trim();
        const cur = current ?? '';
        if (trimmed === cur) return;
        if (trimmed === '') {
          clear.push(key);
        } else {
          (working as Record<string, unknown>)[key] = trimmed;
        }
      };

      setIfStringChanged('displayName', displayName.trim(), vendor.displayName);
      setIfStringChanged('taxId', taxId.trim(), vendor.taxId);
      setIfStringChanged('addressLine1', addressLine1.trim(), vendor.addressLine1);
      setIfNullableChanged('addressLine2', addressLine2, vendor.addressLine2);
      setIfStringChanged('city', city.trim(), vendor.city);
      setIfStringChanged('region', region.trim(), vendor.region);
      setIfStringChanged('postalCode', postalCode.trim(), vendor.postalCode);
      setIfStringChanged('country', country.trim().toUpperCase(), vendor.country);
      setIfNullableChanged('contactEmail', contactEmail, vendor.contactEmail);
      setIfNullableChanged('contactPhone', contactPhone, vendor.contactPhone);

      if (profile) {
        if (paymentTerms !== profile.paymentTerms) working.paymentTerms = paymentTerms;
        if (paymentMethod !== profile.paymentMethod) working.paymentMethod = paymentMethod;
        if (defaultGLAccount.trim() !== profile.defaultGLAccount) {
          working.defaultGLAccount = defaultGLAccount.trim();
        }
        const nextForm1099 =
          form1099Kind === 'REQUIRED'
            ? { kind: 'REQUIRED' as const, formType: form1099FormType }
            : { kind: 'NOT_REQUIRED' as const };
        if (JSON.stringify(nextForm1099) !== JSON.stringify(profile.form1099Status)) {
          working.form1099Status = nextForm1099;
        }
        const nextTax =
          taxKind === 'EXEMPT'
            ? { kind: 'EXEMPT' as const, certificateRef: certificateRef.trim() }
            : { kind: 'TAXABLE' as const };
        if (JSON.stringify(nextTax) !== JSON.stringify(profile.taxExemptionStatus)) {
          working.taxExemptionStatus = nextTax;
        }
      }

      if (clear.length > 0) working.clear = clear;
      const input: UpdateVendorInput = working;

      try {
        const result = await updateVendor(input);
        if (result.__typename === 'UpdateVendorFailure') {
          const msg = `${result.class}: ${JSON.stringify(result.details)}`;
          setError(msg);
          onError(msg);
          setSubmitting(false);
          return;
        }
        onSaved(result.changedFields);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        onError(msg);
        setSubmitting(false);
      }
    },
    [
      vendor,
      profile,
      displayName,
      taxId,
      addressLine1,
      addressLine2,
      city,
      region,
      postalCode,
      country,
      contactEmail,
      contactPhone,
      paymentTerms,
      paymentMethod,
      defaultGLAccount,
      form1099Kind,
      form1099FormType,
      taxKind,
      certificateRef,
      onSaved,
      onError,
    ],
  );

  return (
    <SidePanel
      open
      title={`Edit ${vendor.legalName}`}
      width={640}
      onClose={onClose}
      onSave={() => formRef.current?.requestSubmit()}
      dirty={!submitting}
      saveLabel={submitting ? 'Saving…' : 'Save changes'}
    >
      <form ref={formRef} className="form-grid" onSubmit={onSubmit}>
        <label className="form-row">
          <span className="form-label">Display name</span>
          <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
        </label>
        <label className="form-row">
          <span className="form-label">Tax ID</span>
          <input type="text" value={taxId} onChange={(e) => setTaxId(e.target.value)} required />
        </label>
        <label className="form-row">
          <span className="form-label">Address line 1</span>
          <input type="text" value={addressLine1} onChange={(e) => setAddressLine1(e.target.value)} required />
        </label>
        <label className="form-row">
          <span className="form-label">Address line 2</span>
          <input type="text" value={addressLine2} onChange={(e) => setAddressLine2(e.target.value)} placeholder="Optional" />
        </label>
        <label className="form-row">
          <span className="form-label">City</span>
          <input type="text" value={city} onChange={(e) => setCity(e.target.value)} required />
        </label>
        <label className="form-row">
          <span className="form-label">Region</span>
          <input type="text" value={region} onChange={(e) => setRegion(e.target.value)} required />
        </label>
        <label className="form-row">
          <span className="form-label">Postal code</span>
          <input type="text" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} required />
        </label>
        <label className="form-row">
          <span className="form-label">Country (ISO-2)</span>
          <input type="text" value={country} onChange={(e) => setCountry(e.target.value)} required maxLength={2} />
        </label>
        <label className="form-row">
          <span className="form-label">Contact email</span>
          <input type="text" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="Optional — empty to clear" />
        </label>
        <label className="form-row">
          <span className="form-label">Contact phone</span>
          <input type="text" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="Optional — empty to clear" />
        </label>

        {profile && (
          <>
            <label className="form-row">
              <span className="form-label">Payment terms</span>
              <select value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value as PaymentTerms)} required>
                {PAYMENT_TERMS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </label>
            <label className="form-row">
              <span className="form-label">Payment method</span>
              <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)} required>
                {PAYMENT_METHODS.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </label>
            <label className="form-row">
              <span className="form-label">Default GL account</span>
              <input type="text" value={defaultGLAccount} onChange={(e) => setDefaultGLAccount(e.target.value)} required />
            </label>
            <label className="form-row">
              <span className="form-label">1099 status</span>
              <select value={form1099Kind} onChange={(e) => setForm1099Kind(e.target.value as Form1099Kind)} required>
                <option value="NOT_REQUIRED">Not required</option>
                <option value="REQUIRED">Required</option>
              </select>
            </label>
            {form1099Kind === 'REQUIRED' && (
              <label className="form-row">
                <span className="form-label">1099 form type</span>
                <select value={form1099FormType} onChange={(e) => setForm1099FormType(e.target.value as Form1099FormType)} required>
                  {FORM_1099_FORM_TYPES.map((ft) => (
                    <option key={ft.value} value={ft.value}>{ft.label}</option>
                  ))}
                </select>
              </label>
            )}
            <label className="form-row">
              <span className="form-label">Tax exemption</span>
              <select value={taxKind} onChange={(e) => setTaxKind(e.target.value as TaxExemptionKind)} required>
                <option value="TAXABLE">Taxable</option>
                <option value="EXEMPT">Exempt</option>
              </select>
            </label>
            {taxKind === 'EXEMPT' && (
              <label className="form-row">
                <span className="form-label">Certificate ref</span>
                <input type="text" value={certificateRef} onChange={(e) => setCertificateRef(e.target.value)} required />
              </label>
            )}
          </>
        )}

        {error && <div className="alert alert-neg" role="alert">{error}</div>}
      </form>
    </SidePanel>
  );
}
